import 'mapbox-gl/dist/mapbox-gl.css';
import { useTheme } from 'next-themes';
import React, { type JSX, useEffect, useRef } from 'react';
import type { MapRef } from 'react-map-gl';
import { Map as MapboxMap, Marker } from 'react-map-gl';
import { MAP_BOX_TOKEN } from '@/config/constants';
import useMobileLayout from '@/hooks/use-mobile-layout';
import GoogleMapsLoader from '../../../../../../../../utils/google-maps-loader';
import type { MapMarkerProps } from './components/map-marker';
const DEFAULT_ZOOM = 11;
const MAX_ZOOM = 20;
const MIN_ZOOM = 1;
const FLY_ANIMATION_DURATION = 1000;
// Mapbox skips fitBounds when the padding exceeds the canvas, and the phone map is only ~224px tall.
const FIT_BOUNDS_PADDING = 220;
const FIT_BOUNDS_PADDING_MOBILE = 40;
// Middle of US
const FALLBACK_LATITUDE = 37.09024;
const FALLBACK_LONGITUDE = -95.712891;
type Coords = {
  lat: number;
  lng: number;
};
export type MapProps = {
  markers: JSX.Element[];
  selectedCoords?: Coords;
  onSelect: (id: string) => void;
};
const MapComponent = ({
  markers,
  selectedCoords,
  onSelect
}: MapProps) => {
  const mapRef = useRef<MapRef>(null);
  const theme = useTheme();
  const isDark = theme.theme === 'dark';
  const isMobile = useMobileLayout();
  const fitBoundsPadding = isMobile ? FIT_BOUNDS_PADDING_MOBILE : FIT_BOUNDS_PADDING;
  const handleMarkerClick = (markerProps: MapMarkerProps) => {
    if (!mapRef.current) {
      return;
    }
    mapRef.current.flyTo({
      center: [markerProps.lng, markerProps.lat],
      zoom: DEFAULT_ZOOM,
      duration: FLY_ANIMATION_DURATION
    });
    onSelect(markerProps.id);
  };
  useEffect(() => {
    if (!mapRef.current) {
      return;
    }
    if (selectedCoords) {
      mapRef.current.flyTo({
        center: [selectedCoords.lng, selectedCoords.lat],
        zoom: DEFAULT_ZOOM,
        duration: FLY_ANIMATION_DURATION
      });
    } else if (markers.length) {
      GoogleMapsLoader.importLibrary('core').then(() => {
        // Find the midpoint of all markers and zoom out to see all markers
        const bounds = new google.maps.LatLngBounds();
        markers.forEach(marker => {
          bounds.extend(new google.maps.LatLng(marker.props.lat, marker.props.lng));
        });
        if (!mapRef.current) {
          return;
        }
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();
        mapRef.current.fitBounds([{
          lat: northEast.lat(),
          lng: northEast.lng()
        }, {
          lat: southWest.lat(),
          lng: southWest.lng()
        }], {
          padding: fitBoundsPadding,
          maxZoom: DEFAULT_ZOOM,
          duration: FLY_ANIMATION_DURATION
        });
      });
    } else {
      mapRef.current.flyTo({
        center: [FALLBACK_LONGITUDE, FALLBACK_LATITUDE],
        zoom: MIN_ZOOM,
        duration: FLY_ANIMATION_DURATION
      });
    }
  }, [selectedCoords, markers, fitBoundsPadding]);
  return <div className="flex w-full h-full">
      <MapboxMap ref={mapRef} reuseMaps mapboxAccessToken={MAP_BOX_TOKEN} mapStyle={isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v12'} style={{
      width: '100%',
      height: '100%'
    }} initialViewState={{
      latitude: selectedCoords ? selectedCoords.lat : FALLBACK_LATITUDE,
      longitude: selectedCoords ? selectedCoords.lng : FALLBACK_LONGITUDE,
      zoom: selectedCoords ? DEFAULT_ZOOM : MIN_ZOOM
    }} maxZoom={MAX_ZOOM} minZoom={MIN_ZOOM}>
        {markers.map(marker_0 => <Marker key={marker_0.props.id} longitude={marker_0.props.lng} latitude={marker_0.props.lat} anchor="bottom" style={marker_0.props.isSelected ? {
        zIndex: 20
      } : {
        zIndex: 0
      }}>
            {React.cloneElement(marker_0, {
          onClick: () => handleMarkerClick(marker_0.props)
        })}
          </Marker>)}
      </MapboxMap>
    </div>;
};
export default MapComponent;